import React from 'react';
import { useAlertModal } from '../AlertModalContext';

/* ── Configuração visual por variante ── */
const variantConfig = {
  success: {
    icon: '✓',
    iconBg: '#f0fdf4',
    iconColor: '#16a34a',
    iconBorder: '#bbf7d0',
    titleColor: '#15803d',
    btnBg: 'linear-gradient(135deg, #22c55e, #16a34a)',
    btnShadow: 'rgba(34,197,94,0.35)',
    headerBorder: '#bbf7d0',
    label: 'Sucesso',
  },
  danger: {
    icon: '✕',
    iconBg: '#fef2f2',
    iconColor: '#dc2626',
    iconBorder: '#fecaca',
    titleColor: '#b91c1c',
    btnBg: 'linear-gradient(135deg, #ef4444, #dc2626)',
    btnShadow: 'rgba(239,68,68,0.35)',
    headerBorder: '#fecaca',
    label: 'Erro',
  },
  warning: {
    icon: '!',
    iconBg: '#fffbeb',
    iconColor: '#d97706',
    iconBorder: '#fde68a',
    titleColor: '#b45309',
    btnBg: 'linear-gradient(135deg, #f59e0b, #d97706)',
    btnShadow: 'rgba(245,158,11,0.35)',
    headerBorder: '#fde68a',
    label: 'Atenção',
  },
  info: {
    icon: 'i',
    iconBg: '#eff6ff',
    iconColor: '#2563eb',
    iconBorder: '#bfdbfe',
    titleColor: '#1d4ed8',
    btnBg: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    btnShadow: 'rgba(59,130,246,0.35)',
    headerBorder: '#bfdbfe',
    label: 'Informação',
  },
};

const AlertModalComponent = () => {
  const { modalState, hideAlertModal } = useAlertModal();

  if (!modalState.show) return null;

  const cfg = variantConfig[modalState.variant] || variantConfig.info;
  const title   = modalState.title   || cfg.label;
  const message = typeof modalState.message === 'string'
    ? modalState.message
    : JSON.stringify(modalState.message);

  return (
    <>
      {/* ── CSS injetado ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .am-backdrop {
          position: fixed; inset: 0; z-index: 1060;
          background: rgba(0,0,0,0.45);
          backdrop-filter: blur(3px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
          animation: amFadeIn 0.2s ease;
        }
        .am-modal {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.18), 0 8px 20px rgba(0,0,0,0.08);
          width: 100%;
          max-width: 420px;
          font-family: 'Inter','Segoe UI',sans-serif;
          overflow: hidden;
          animation: amSlideUp 0.25s cubic-bezier(0.4,0,0.2,1);
        }
        .am-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px 14px;
          border-bottom: 1px solid;
        }
        .am-close-btn {
          width: 28px; height: 28px; border-radius: 50%;
          border: 1px solid #e5e7eb; background: #f9fafb;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          font-size: calc(13px * var(--font-scale)) color: #6b7280; line-height: 1;
          transition: background 0.15s, color 0.15s;
          flex-shrink: 0;
        }
        .am-close-btn:hover { background: #f3f4f6; color: #374151; }
        .am-body { padding: 24px 20px; text-align: center; }
        .am-icon {
          width: 52px; height: 52px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 16px;
          font-size: 20px; font-weight: 800;
          border: 2px solid;
          flex-shrink: 0;
        }
        .am-title {
          font-size: 16px; font-weight: 700; margin: 0 0 8px;
          line-height: 1.3;
        }
        .am-message {
          font-size: calc(13px * var(--font-scale)) color: #6b7280; margin: 0;
          line-height: 1.6; word-break: break-word;
        }
        .am-footer {
          padding: 0 20px 20px;
          display: flex; justify-content: center;
        }
        .am-btn {
          padding: 10px 32px; border-radius: 9px; border: none;
          color: #fff; font-size: calc(13px * var(--font-scale)) font-weight: 600;
          cursor: pointer; font-family: inherit;
          transition: opacity 0.15s, transform 0.1s;
          min-width: 100px;
        }
        .am-btn:hover  { opacity: 0.88; transform: translateY(-1px); }
        .am-btn:active { opacity: 1;    transform: translateY(0);    }

        @keyframes amFadeIn   { from { opacity: 0; }                        to { opacity: 1; }                        }
        @keyframes amSlideUp  { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ── Backdrop ── */}
      <div className="am-backdrop" onClick={hideAlertModal}>

        {/* ── Modal ── */}
        <div className="am-modal" onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div
            className="am-header"
            style={{ borderBottomColor: cfg.headerBorder }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Mini dot colorido */}
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%',
                backgroundColor: cfg.iconColor, flexShrink: 0,
                boxShadow: `0 0 0 3px ${cfg.iconBorder}`,
              }} />
              <span style={{
                fontSize: `calc(14px * var(--font-scale))`, fontWeight: '700',
                color: cfg.titleColor, lineHeight: 1,
              }}>
                {title}
              </span>
            </div>
            <button className="am-close-btn" onClick={hideAlertModal} aria-label="Fechar">
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="am-body">
            {/* Ícone */}
            <div
              className="am-icon"
              style={{
                backgroundColor: cfg.iconBg,
                borderColor:     cfg.iconBorder,
                color:           cfg.iconColor,
              }}
            >
              {cfg.icon}
            </div>

            {/* Mensagem */}
            <p className="am-message">{message}</p>
          </div>

          {/* Footer */}
          <div className="am-footer">
            <button
              className="am-btn"
              style={{
                background:  cfg.btnBg,
                boxShadow:   `0 3px 10px ${cfg.btnShadow}`,
              }}
              onClick={hideAlertModal}
            >
              OK
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default AlertModalComponent;
